import { useEffect, useState } from "react";

import { SentryConfig } from "../types";
import { SentryDSN } from "./../constants";

// define warning ids to show enforcement modals based on the response from api;
// warning types returned by api: https://github.com/trustpilot/BusinessUnitCompliance/blob/master/BusinessUnitComplianceCore/Data/Mongo/Shared/BusinessUnitWarningEventType.cs
export const translateEnforcementWarnings = (warningsFromApi) => {
  const warnings = [];
  warningsFromApi
    .filter((warning) => !warning.acknowledged)
    .forEach((warning) => {
      switch (warning.type) {
        case "BiasedInvitation":
        case "CherryPicking":
        case "IncentiveToChange":
        case "IncentiveToWritePositiveReviews":
        case "FabricatedReviews":
        case "EducationalFabricatedReviews":
        case "CeaseAndDesistFabricatedReviews":
        case "FurtherActionFabricatedReviews":
        case "MisuseOfReportingFunction":
        case "BiasedReportingForPersonalInformation":
        case "ThreatToChangeOrDeleteNegativeReviews":
        case "MisuseOfPublicReplyFunction":
          warnings.push({
            type: warning.type,
            id: warning.warningId,
            details: warning.details,
          });
          break;

        case "ProfileViolation1":
        case "ProfileViolation2":
          warnings.push({
            type: "ProfileViolation",
            id: warning.warningId,
            details: warning.details,
          });
          break;

        default:
          break;
      }
    });

  return warnings;
};

// check if string contains numbers to avoid setting b.unit.id or review.id as a tag
function hasNumber(str: string) {
  return /\d/.test(str);
}

function extractPathFromUrl(url) {
  if (!url) {
    return null;
  }
  const v1 = "v1";
  const slashDelimiter = "/";
  const idReplacementString = "{id}";
  try {
    const urlObject = new URL(url);
    const urlParts = urlObject.pathname.split(slashDelimiter);
    return urlParts
      .map((item: string) => (item !== v1 && hasNumber(item) ? idReplacementString : item))
      .join(slashDelimiter);
  } catch (err) {
    return null;
  }
}

export const shellSentryConfig = {
  dsn: SentryDSN,
  release: `businessapp-shell@#{Octopus.Release.Number}`,
};

export const initializeSentry = (businessUserId: string, appSentryConfig?: SentryConfig) => {
  const config = appSentryConfig || shellSentryConfig;
  if (window.Sentry) {
    window.Sentry.init({
      dsn: config.dsn,
      release: config.release,
      environment: "#{Environment}",
      beforeSend: function (event) {
        if (event.exception && event.exception.values) {
          for (const exception of event.exception.values) {
            if (exception.value.match(/ResizeObserver loop limit exceeded/i)) {
              return null;
            }
          }
        }
        return event;
      },
    });

    if (businessUserId) {
      window.Sentry.configureScope((scope) => {
        scope.setUser({ id: businessUserId });
      });
    }
  }
};

export const logToSentry = (error: any) => {
  const Sentry = (window as any).Sentry;

  if (Sentry) {
    Sentry.withScope((scope) => {
      const application = "appshell";
      if (error.isAxiosError && error.response) {
        // if it is axios error, set fingerprint for grouping errors by method, path and errorCode:
        const method = error.config.method;
        const code = error.response?.status?.toString();
        const path = extractPathFromUrl(error.config.url);

        scope.setFingerprint([method, path, code, application]);
      } else {
        // otherwise add application name to default fingerprint:
        scope.setFingerprint(["{{ default }}", application]);
      }
      Sentry.captureException(error);
    });
  }
};

export const useMediaQuery = (query: string) => {
  const [mqMatched, setMqMatched] = useState(null);
  const mq = window.matchMedia(query);
  const listener = (evt: MediaQueryListEvent) => {
    setMqMatched(evt.matches);
  };

  useEffect(() => {
    setMqMatched(mq.matches);
    mq.addEventListener("change", listener);
    return () => {
      mq.removeEventListener("change", listener);
    };
  }, []);

  return mqMatched;
};
